/**
 * OnboardingFloatingWidget
 * 
 * Self-contained floating widget for onboarding that manages its own visibility.
 * Includes both the minimized widget and the full wizard modal.
 * Placed in App.tsx alongside other floating widgets for DOM consistency.
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useConsent } from '../../contexts/ConsentContext';
import { useOnboardingState, isOnboardingStarted as isOnboardingStartedFromStorage, hasCompletedOnboarding as hasCompletedOnboardingFromStorage } from '../../hooks/useOnboardingState';
import { OnboardingWizard } from './OnboardingWizard';
import { apiClient } from '../../lib/api';

export function OnboardingFloatingWidget() {
  const { user, verifyToken } = useAuth();
  const { hasDecided: consentDecided } = useConsent();
  const { onboardingState } = useOnboardingState();
  
  const [showWizard, setShowWizard] = useState(false);
  const [showWidget, setShowWidget] = useState(false);
  const [widgetDismissed, setWidgetDismissed] = useState(false);
  const [manuallyDismissed, setManuallyDismissed] = useState(false);

  // Check if onboarding should be shown (only on initial load)
  // Wait for consent decision first to avoid overlapping modals
  useEffect(() => {
    if (manuallyDismissed) return;
    if (!consentDecided) return; // Don't show onboarding until consent is decided
    
    const isCompleted = hasCompletedOnboardingFromStorage() || user?.onboarding_completed;
    
    if (isCompleted) return;
    
    // Show minimized widget after delay - let user see the interface first
    // User can click to open full wizard when ready (less intrusive)
    const timer = setTimeout(() => {
      const stillNotCompleted = !hasCompletedOnboardingFromStorage() && !user?.onboarding_completed;
      if (stillNotCompleted && !manuallyDismissed && !widgetDismissed) {
        setShowWidget(true);
      }
    }, 2000); // 2 second delay so user can see the interface
    return () => clearTimeout(timer);
  }, [user, widgetDismissed, manuallyDismissed, onboardingState.completed, consentDecided]);

  // Monitor onboarding completion to hide widget
  useEffect(() => {
    const isCompleted = hasCompletedOnboardingFromStorage();
    if (isCompleted) {
      setShowWidget(false);
      setShowWizard(false);
      setWidgetDismissed(false);
    }
  }, [onboardingState.completed]);

  const shouldShowWidget = showWidget && !widgetDismissed && !showWizard && !hasCompletedOnboardingFromStorage();

  const handleWidgetOpen = () => {
    setShowWidget(false);
    setManuallyDismissed(false);
    setShowWizard(true);
  };

  const handleWidgetDismiss = () => {
    setShowWidget(false);
    setWidgetDismissed(true);
  };

  const handleWizardComplete = async () => {
    setShowWizard(false);
    setShowWidget(false);
    setManuallyDismissed(true);
    
    // If user is logged in, sync onboarding data to their account
    if (user) {
      try {
        await apiClient.preferences.updateUserPreferences({
          user_type: onboardingState.userType || undefined,
          native_language: onboardingState.nativeLanguage || undefined,
          preferred_study_language: onboardingState.targetLanguage || undefined,
          preferred_cefr_level: onboardingState.cefrLevel || undefined,
          onboarding_completed: true,
        });
        await verifyToken();
      } catch (error) {
        console.error('Failed to sync onboarding preferences:', error);
      }
    }
  };

  const handleWizardSkip = () => {
    setShowWizard(false);
    setManuallyDismissed(true);
    
    // Show widget if user has started onboarding
    if (isOnboardingStartedFromStorage()) {
      setTimeout(() => setShowWidget(true), 500);
    }
  };

  return (
    <>
      {/* Full Wizard Modal - rendered via portal to escape FloatingWidgetsContainer stacking context */}
      {showWizard && createPortal(
        <OnboardingWizard
          onComplete={handleWizardComplete}
          onSkip={handleWizardSkip}
        />,
        document.body
      )}

      {/* Minimized Widget */}
      {shouldShowWidget && (
        <div className="onboarding-widget w-80">
          <div className="widget-card relative rounded-lg bg-white dark:bg-gray-800 p-4 shadow-lg border border-gray-200 dark:border-gray-700">
            {/* Dismiss button */}
            <button
              onClick={handleWidgetDismiss}
              className="dismiss-btn absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Dismiss widget"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Content */}
            <div className="widget-content pr-6">
              <div className="flex items-start gap-3 mb-3">
                <div className="icon-wrapper rounded-full bg-primary/10 p-2 flex-shrink-0">
                  <BookOpen className="h-5 w-5 text-primary" />
                </div>
                <div className="text-content">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    Complete your reading preferences
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Get personalized book recommendations
                  </p>
                </div>
              </div>

              <button
                onClick={handleWidgetOpen}
                className="complete-btn w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 transition-colors"
              >
                Continue Setup
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

